import React from 'react';
import { Button } from '@/components/ui/button';
import { Linkedin, ChevronDown, Server, Network, Cloud } from 'lucide-react';

export const HeroSection = () => {
  const handleScrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center hero-pattern overflow-hidden">
      {/* Floating decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/30 rounded-full blur-3xl" />
        
        {/* Floating icons */}
        <div className="absolute top-1/3 right-1/4 text-primary/20 animate-bounce-subtle" style={{ animationDelay: '0s' }}>
          <Server size={32} />
        </div>
        <div className="absolute bottom-1/3 left-1/4 text-primary/20 animate-bounce-subtle" style={{ animationDelay: '0.5s' }}>
          <Network size={28} />
        </div>
        <div className="absolute top-1/2 right-1/3 text-primary/20 animate-bounce-subtle" style={{ animationDelay: '1s' }}>
          <Cloud size={36} />
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20 md:py-0">
        {/* Status badge - Set showOpenToWork to true when actively seeking opportunities */}
        {(() => {
          const showOpenToWork = false; // Toggle this to show/hide the badge
          return showOpenToWork ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-soft rounded-full text-sm font-medium text-primary mb-8 animate-fade-in-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Open to Opportunities
            </div>
          ) : null;
        })()}

        {/* Name */}
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4 animate-fade-in-up stagger-1">
          Narek Onanyan
        </h1>

        {/* Title */}
        <h2 className="text-lg sm:text-xl lg:text-2xl text-muted-foreground mb-6 animate-fade-in-up stagger-2">
          <span className="text-primary font-medium">IT Support Engineer</span>
          <span className="mx-2 text-border">|</span>
          <span>Infrastructure & Network Operations</span>
        </h2>

        {/* Summary */}
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up stagger-3">
          Hands-on enterprise IT infrastructure support with a strong focus on deployment, 
          networking, and troubleshooting in large-scale operational environments.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up stagger-4">
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => handleScrollToSection('#experience')}
          >
            View Experience
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            asChild
          >
            <a 
              href="https://www.linkedin.com/in/narekonanyan" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Linkedin size={18} />
              LinkedIn Profile
            </a>
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={() => handleScrollToSection('#about')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-primary transition-colors animate-bounce-subtle"
        aria-label="Scroll to content"
      >
        <ChevronDown size={32} />
      </button>
    </section>
  );
};

export default HeroSection;
